/**
 * FuelLane — Renders FUEL_ON_VEHICLE graph edges
 *
 * Lane 4: Instantaneous events as markers, time-ranged as short windows.
 */
import type { FuelOverlay, TimelineSelection, TimelineHover } from '../../../types/vehicleSceneTimeline';
import {
  timeWindowToRect,
  msToX,
  toMs,
  formatTime,
  formatConfidence,
  AUTHORITY_COLORS,
  type TimeScale,
} from '../../../utils/timelineUtils';

interface FuelLaneProps {
  fuel: FuelOverlay[];
  scale: TimeScale;
  selection: TimelineSelection | null;
  hover: TimelineHover | null;
  onSelect: (edgeId: string) => void;
  onHover: (h: TimelineHover | null) => void;
}

const LANE_HEIGHT = 36;
const MARKER_THRESHOLD_MS = 10 * 60_000; // events < 10min shown as markers

export default function FuelLane({
  fuel,
  scale,
  selection,
  hover: _hover,
  onSelect,
  onHover,
}: FuelLaneProps) {
  const isHighlighted = (edgeId: string): boolean => {
    if (!selection) return false;
    if (selection.type === 'fuel' && selection.id === edgeId) return true;
    return selection.relatedFuelIds?.includes(edgeId) ?? false;
  };

  const isDimmed = (edgeId: string): boolean => {
    if (!selection) return false;
    return !isHighlighted(edgeId);
  };

  return (
    <div className="vst-lane" data-lane="fuel">
      <div className="vst-lane-label">
        <span className="vst-lane-dot" style={{ background: '#f59e0b' }} />
        Fuel <span className="vst-lane-badge">graph</span>
      </div>
      <svg className="vst-lane-svg" width={scale.widthPx} height={LANE_HEIGHT}>
        {fuel.map(f => {
          const durationMs = toMs(f.to_ts) - toMs(f.from_ts);
          const isMarker = durationMs < MARKER_THRESHOLD_MS;
          const highlighted = isHighlighted(f.edge_id);
          const dimmed = isDimmed(f.edge_id);
          const color = AUTHORITY_COLORS[f.authority_level] || '#f59e0b';
          const opacity = dimmed ? 0.2 : 0.9;

          if (isMarker) {
            // Render as diamond marker
            const cx = msToX(toMs(f.from_ts) + durationMs / 2, scale);
            const cy = LANE_HEIGHT / 2;
            const size = 8;
            return (
              <g
                key={f.edge_id}
                className="vst-lane-item"
                onClick={() => onSelect(f.edge_id)}
                onMouseEnter={e =>
                  onHover({
                    type: 'fuel',
                    id: f.edge_id,
                    mouseX: e.clientX,
                    mouseY: e.clientY,
                  })
                }
                onMouseLeave={() => onHover(null)}
                style={{ cursor: 'pointer' }}
              >
                <polygon
                  points={`${cx},${cy - size} ${cx + size},${cy} ${cx},${cy + size} ${cx - size},${cy}`}
                  fill={color}
                  opacity={opacity}
                  stroke={highlighted ? '#fff' : 'none'}
                  strokeWidth={highlighted ? 2 : 0}
                />
                {f.fuel_litres && (
                  <text
                    x={cx + size + 4}
                    y={cy + 1}
                    fill="#d1d5db"
                    fontSize={9}
                    dominantBaseline="middle"
                    pointerEvents="none"
                  >
                    {f.fuel_litres}L
                  </text>
                )}
                <title>
                  {`${f.label || f.fuel_event_type || 'Fuel'}\nEdge: ${f.edge_id}\nTime: ${formatTime(toMs(f.from_ts))}${f.fuel_litres ? '\nQuantity: ' + f.fuel_litres + 'L' : ''}\nConfidence: ${formatConfidence(f.confidence)}\nAuthority: ${f.authority_level}`}
                </title>
              </g>
            );
          }

          // Render as window
          const { x, width } = timeWindowToRect(f.from_ts, f.to_ts, scale);
          return (
            <g
              key={f.edge_id}
              className="vst-lane-item"              pointerEvents="auto"              onClick={() => onSelect(f.edge_id)}
              onMouseEnter={e =>
                onHover({
                  type: 'fuel',
                  id: f.edge_id,
                  mouseX: e.clientX,
                  mouseY: e.clientY,
                })
              }
              onMouseLeave={() => onHover(null)}
              style={{ cursor: 'pointer' }}
            >
              <rect
                x={x}
                y={6}
                width={width}
                height={LANE_HEIGHT - 12}
                rx={3}
                fill={color}
                opacity={opacity}
                stroke={highlighted ? '#fff' : color}
                strokeWidth={highlighted ? 2 : 1}
              />
              <title>
                {`${f.label || f.fuel_event_type || 'Fuel'}\nEdge: ${f.edge_id}\n${formatTime(toMs(f.from_ts))} – ${formatTime(toMs(f.to_ts))}${f.fuel_litres ? '\nQuantity: ' + f.fuel_litres + 'L' : ''}\nConfidence: ${formatConfidence(f.confidence)}\nAuthority: ${f.authority_level}`}
              </title>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
